import React, { useEffect, useState } from 'react';
import './OrganizeExam.css';
import { formatDateTimeForInput } from '../utils/dateUtils';

const OrganizeExam = () => {
  const [examId, setExamId] = useState('');
  const [level, setLevel] = useState('');
  const [levels, setLevels] = useState([]);
  const [modules, setModules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(20);
  const [examName, setExamName] = useState('');  // Add exam name state
  const [message, setMessage] = useState('');
  const [nonModular, setNonModular] = useState(false);
  const [pauseAfterRounds, setPauseAfterRounds] = useState(4);  // Default pause after 4 rounds
  const [pauseDuration, setPauseDuration] = useState(30);  // Default pause duration 30 minutes
  const [thirdRoundRoomId, setThirdRoundRoomId] = useState('');  // Room for third round in A1/A2 Schriftlich

  const BASEURL = process.env.REACT_APP_API_URL

  // Load level options
  useEffect(() => {
    fetch(`${BASEURL}/levels`)
      .then(res => res.json())
      .then(data => setLevels(data));
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('goethe_exam_results');
    if (saved) {
        try {
        const parsed = JSON.parse(saved);
        if (parsed.exam_id) {
            setExamId(parsed.exam_id);
        }
        } catch (err) {
        console.error('Failed to parse exam_id from localStorage:', err);
        }
    }
    }, []);

  // Load modules and rooms when level changes
  useEffect(() => {
    if (!level) return;

    fetch(`${BASEURL}/modules?level=${level}`)
      .then(res => res.json())
      .then(data => setModules(data));
  }, [level]);

  useEffect(() => {
  if (!level || !examId) return;

  const fetchRooms = async () => {
    try {
      let url = `${BASEURL}/rooms?level=${level}&exam_id=${examId}`;
      
      if (!nonModular && selectedModules.length > 0) {
        const moduleQuery = selectedModules.map(m => `module=${m}`).join('&');
        url += `&${moduleQuery}`;
      } else if (!nonModular) {
        setRooms([]);
        return;
      }

      const res = await fetch(url);
      const data = await res.json();
      setRooms(data.filter(room => room.remaining > 0));
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  fetchRooms();
}, [level, examId, selectedModules, nonModular]);


  const handleCheckboxChange = (id, setter, values) => {
    if (values.includes(id)) {
      setter(values.filter(v => v !== id));
    } else {
      setter([...values, id]);
    }
  };

  // Check if we should show the third round room selector
  const isA1A2Schriftlich = () => {
    if (nonModular || !level || selectedModules.length === 0 || modules.length === 0) {
      return false;
    }
    
    // Use loose equality (==) to handle string/number mismatch
    const currentLevel = levels.find(lvl => lvl.id == level);
    const levelName = currentLevel ? currentLevel.name : '';
    
    if (!['A1', 'A2'].includes(levelName)) {
      return false;
    }
    
    const selectedModuleNames = modules
      .filter(mod => selectedModules.includes(mod.id))
      .map(mod => mod.name);
    
    return selectedModuleNames.includes('Schriftlich');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

  if (!examId || !level || !startTime || !duration || selectedRooms.length === 0) {
    setMessage('Please complete all fields');
    return;
  }

  if (!examName.trim()) {
    setMessage('Please enter an exam name');
    return;
  }

  if (!nonModular && selectedModules.length === 0) {
    setMessage('Please select at least one module or check Non Modular Exam');
    return;
  }

  if (isA1A2Schriftlich() && !thirdRoundRoomId) {
    setMessage('Please select a room for the third round (A1/A2 Schriftlich)');
    return;
  }

    try {
      const payload = {
        exam_id: examId,
        level_id: level,
        module_ids: selectedModules,
        room_ids: selectedRooms,
        start_time: startTime,
        duration_minutes: duration,
        non_modular: nonModular,
        exam_name: examName, // Add exam name to payload
        pause_after_rounds: pauseAfterRounds, // Add pause after rounds to payload
        pause_duration: pauseDuration, // Add pause duration to payload
        third_round_room_id: thirdRoundRoomId ? parseInt(thirdRoundRoomId) : null // Convert to integer
      };

      console.log('Sending payload with third_round_room_id:', payload.third_round_room_id);

      const res = await fetch(`${BASEURL}/organize-exam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      setMessage(result.message || 'Exam organized!');
    } catch (err) {
      console.error(err);
      setMessage('Error organizing exam');
    }
  };

  const formatDateToInput = (date) => {
    return formatDateTimeForInput(date);
  };

  // Helper function to get default start time based on selected modules and level
  const getDefaultTimeForModules = () => {
    if (selectedModules.length === 0) {
      return { hours: 8, minutes: 30 }; // Default to 08:30 if no modules selected
    }

    // Get module names for selected modules
    const selectedModuleNames = modules
      .filter(mod => selectedModules.includes(mod.id))
      .map(mod => mod.name);

    // Get the current level name
    const currentLevel = levels.find(lvl => lvl.id == level);
    const levelName = currentLevel ? currentLevel.name : '';

    // Define default times for each module based on level
    const levelModuleTimeMap = {
      'A1': {
        'Schriftlich': { hours: 8, minutes: 30 },
        'Mundlich': { hours: 13, minutes: 10 }
      },
      'A2': {
        'Schriftlich': { hours: 8, minutes: 30 },
        'Mundlich': { hours: 13, minutes: 10 },
        'Sprechen': { hours: 13, minutes: 10 }
      },
      'B1': {
        'Lesen': { hours: 8, minutes: 30 },
        'Horen': { hours: 9, minutes: 45 },
        'Schreiben': { hours: 10, minutes: 35 },
        'Sprechen': { hours: 13, minutes: 10 }
      },
      'B2': {
        'Lesen': { hours: 8, minutes: 30 },
        'Horen': { hours: 9, minutes: 45 },
        'Schreiben': { hours: 10, minutes: 35 },
        'Sprechen': { hours: 13, minutes: 10 }
      },
      'C1': {
        'Lesen': { hours: 8, minutes: 30 },
        'Horen': { hours: 9, minutes: 45 },
        'Schreiben': { hours: 10, minutes: 35 },
        'Sprechen': { hours: 13, minutes: 10 }
      },
      'C2': {
        'Lesen': { hours: 8, minutes: 30 },
        'Horen': { hours: 10, minutes: 0 },
        'Schreiben': { hours: 10, minutes: 50 },
        'Sprechen': { hours: 13, minutes: 10 }
      }
    };

    // Default times if level is not in the map above
    const defaultModuleTimeMap = {
      'Schriftlich': { hours: 8, minutes: 30 },
      'Mundlich': { hours: 13, minutes: 10 },
      'Lesen': { hours: 8, minutes: 30 },
      'Horen': { hours: 9, minutes: 45 },
      'Schreiben': { hours: 10, minutes: 35 },
      'Sprechen': { hours: 13, minutes: 10 }
    };

    const moduleTimeMap = levelModuleTimeMap[levelName] || defaultModuleTimeMap;

    // Priority order for modules (if multiple modules are selected)
    const priorityOrder = ['Schriftlich', 'Lesen', 'Horen', 'Schreiben', 'Sprechen', 'Mundlich'];

    // Find the highest priority module that's selected
    for (const moduleName of priorityOrder) {
      if (selectedModuleNames.includes(moduleName)) {
        return moduleTimeMap[moduleName] || { hours: 8, minutes: 30 };
      }
    }

    // Fallback to 08:30 if no matching module found
    return { hours: 8, minutes: 30 };
  };

  // Helper function to get default duration based on selected modules and level
  const getDefaultDurationForModules = () => {
    if (selectedModules.length === 0) {
      return 20; // Default duration if no modules selected
    }

    // Get module names for selected modules
    const selectedModuleNames = modules
      .filter(mod => selectedModules.includes(mod.id))
      .map(mod => mod.name);

    // Get the current level name
    const currentLevel = levels.find(lvl => lvl.id == level);
    const levelName = currentLevel ? currentLevel.name : '';

    // Define default durations for each module based on level
    const levelModuleDurationMap = {
      'A1': {
        'Schriftlich': 65,
        'Mundlich': 20
      },
      'A2': {
        'Schriftlich': 90,
        'Mundlich': 20,
        'Sprechen': 20
      },
      'B1': {
        'Lesen': 65,
        'Horen': 40,
        'Schreiben': 60,
        'Sprechen': 20
      },
      'B2': {
        'Lesen': 65,
        'Horen': 40,
        'Schreiben': 75,
        'Sprechen': 20
      },
      'C1': {
        'Lesen': 65,
        'Horen': 40,
        'Schreiben': 75,
        'Sprechen': 20
      },
      'C2': {
        'Lesen': 80,
        'Horen': 40,
        'Schreiben': 80,
        'Sprechen': 20
      }
    };

    // Default durations if level is not in the map above
    const defaultModuleDurationMap = {
      'Schriftlich': 65,
      'Mundlich': 20,
      'Lesen': 65,
      'Horen': 35,
      'Schreiben': 75,
      'Sprechen': 20
    };

    const moduleDurationMap = levelModuleDurationMap[levelName] || defaultModuleDurationMap;

    // Priority order for modules (if multiple modules are selected)
    const priorityOrder = ['Schriftlich', 'Lesen', 'Horen', 'Schreiben', 'Sprechen', 'Mundlich'];

    // Find the highest priority module that's selected
    for (const moduleName of priorityOrder) {
      if (selectedModuleNames.includes(moduleName)) {
        return moduleDurationMap[moduleName] || 20;
      }
    }

    // Fallback to 20 if no matching module found
    return 20;
  };

  useEffect(() => {
    const now = new Date();
    now.setHours(8, 0, 0, 0); // Set to 08:00 AM today
    setStartTime(formatDateToInput(now));
  }, []);

  // Update start time when modules or level are selected
  useEffect(() => {
    if (modules.length === 0 || levels.length === 0) return; // Wait for modules and levels to be loaded
    
    const defaultTime = getDefaultTimeForModules();
    const currentDate = startTime ? new Date(startTime) : new Date();
    
    // Preserve the current date but update the time
    currentDate.setHours(defaultTime.hours, defaultTime.minutes, 0, 0);
    setStartTime(formatDateToInput(currentDate));
  }, [selectedModules, modules, level, levels]); // Trigger when selectedModules, modules, level, or levels change

  // Update duration when modules or level are selected
  useEffect(() => {
    if (modules.length === 0 || levels.length === 0) return; // Wait for modules and levels to be loaded
    
    const defaultDuration = getDefaultDurationForModules();
    setDuration(defaultDuration);
  }, [selectedModules, modules, level, levels]); // Trigger when selectedModules, modules, level, or levels change

  return (
    <div className="organize-container">
      <h2>Organize Exam</h2>
      <form onSubmit={handleSubmit}>
        <label>Exam ID:</label>
        <input
          type="text"
          value={examId}
          onChange={(e) => setExamId(e.target.value)}
          placeholder="Paste exam ID here"
          required
        />

        <label>Exam Name:</label>
        <input
          type="text"
          value={examName}
          onChange={(e) => setExamName(e.target.value)}
          placeholder="Enter exam name (e.g., Goethe A1 Exam, B2 Test)"
          required
        />

        <label>Level:</label>
        <select value={level} onChange={(e) => setLevel(e.target.value)} required>
          <option value="">-- Select Level --</option>
          {levels.map((lvl) => (
            <option key={lvl.id} value={lvl.id}>{lvl.name}</option>
          ))}
        </select>

        <label>Start Time:</label>
        <input
          type="datetime-local"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          required
        />

        <label>Duration (minutes):</label>
        <input
          type="number"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          min={1}
          required
        />

        <label>Pause after which rounds (for Sprechen exams):</label>
        <input
          type="number"
          value={pauseAfterRounds}
          onChange={(e) => setPauseAfterRounds(parseInt(e.target.value) || 4)}
          min={1}
          max={20}
          placeholder="4"
        />
        <small style={{color: '#666', fontSize: '12px', display: 'block', marginTop: '4px'}}>
          Enter how many rounds to run before adding a pause (e.g., 3 = pause after every 3rd round)
        </small>

        <label>Pause duration (minutes):</label>
        <input
          type="number"
          value={pauseDuration}
          onChange={(e) => setPauseDuration(parseInt(e.target.value) || 30)}
          min={1}
          max={120}
          placeholder="30"
        />
        <small style={{color: '#666', fontSize: '12px', display: 'block', marginTop: '4px'}}>
          Duration of the break between rounds in minutes (default: 30 minutes)
        </small>

<label>Modules:</label>
<div className="checkbox-grid">
  <label>
    <input
      type="checkbox"
      checked={nonModular}
      onChange={() => {
        setNonModular(!nonModular);
        setSelectedModules([]); // clear selected modules if toggled
      }}
    />
    Non Modular Exam
  </label>

{!nonModular && modules
  .filter(mod => mod.name !== "Non Modular Exam")  // filter it out here
  .map((mod) => (
    <label key={mod.id}>
      <input
        type="checkbox"
        checked={selectedModules.includes(mod.id)}
        onChange={() => handleCheckboxChange(mod.id, setSelectedModules, selectedModules)}
      />
      {mod.name}
    </label>
  ))
}

</div>


<label>Rooms:</label>
<div className="checkbox-grid">
  {/* Select All checkbox */}
  <label>
    <input
      type="checkbox"
      checked={selectedRooms.length === rooms.length && rooms.length > 0}
      onChange={(e) => {
        if (e.target.checked) {
          setSelectedRooms(rooms.map(room => room.id));
        } else {
          setSelectedRooms([]);
        }
      }}
    />
    Select All Rooms
  </label>

  {rooms.map((room) => (
    <label key={room.id}>
      <input
        type="checkbox"
        checked={selectedRooms.includes(room.id)}
        onChange={() => handleCheckboxChange(room.id, setSelectedRooms, selectedRooms)}
        disabled={room.remaining <= 0} // disable full rooms
      />
      {room.name} ({room.module}) - {room.remaining} / {room.capacity} seats
    </label>
  ))}
</div>

{isA1A2Schriftlich() && (
  <>
    <label>Third Round Room (for A1/A2 Schriftlich):</label>
    <select 
      value={thirdRoundRoomId} 
      onChange={(e) => {
        const selectedId = e.target.value;
        const selectedRoom = rooms.find(r => r.id == selectedId);
        console.log('Selected room for third round:', {
          id: selectedId,
          name: selectedRoom?.name,
          module: selectedRoom?.module,
          allRooms: rooms.map(r => ({id: r.id, name: r.name, module: r.module, selected: selectedRooms.includes(r.id)}))
        });
        setThirdRoundRoomId(selectedId);
      }} 
      required
    >
      <option value="">-- Select Room for Third Round --</option>
      {rooms
        .filter(room => {
          const isSelected = selectedRooms.includes(room.id);
          const isSchriftlich = room.module === 'Schriftlich';
          console.log(`Room ${room.name}: selected=${isSelected}, isSchriftlich=${isSchriftlich}, module=${room.module}`);
          return isSelected && isSchriftlich;
        })
        .map((room) => (
          <option key={room.id} value={room.id}>
            {room.name} (ID: {room.id})
          </option>
        ))}
    </select>
    <small style={{color: '#666', fontSize: '12px', display: 'block', marginTop: '4px'}}>
      Select which Schriftlich room should be used for the third round (13:30).
      The first two rounds will use all selected Schriftlich rooms.
    </small>
  </>
)}

        <button type="submit">Organize</button>
      </form>

      {message && <div className="message-box">{message}</div>}
    </div>
  );
};

export default OrganizeExam;
